'use client';

import { createDebug } from '@shared/debug';
import { useFullStoryTrackEvent } from '@shared/tracking';
import type { Currency } from '@shared/utils';
import { useCallback } from 'react';
import type { DepositSuccessData } from '../../DepositSheet/types';
import type { CartResponse, CheckoutTicketMeta } from '../types';
import { getOrderCompletedPayload } from '../utils/getOrderCompletedPayload';
import { useCheckoutSideEffects } from './useCheckoutSideEffects';

const debug = createDebug({ isEnabled: false });

function useCallSubmitCart({
  submitCart,
  currency,
  ticketMeta,
  onSuccess,
  onOrderSubmitted,
  redirectToThankYouPage,
}: {
  submitCart: () => Promise<CartResponse>;
  currency: Currency;
  ticketMeta?: CheckoutTicketMeta;
  onSuccess?: () => void;
  onOrderSubmitted?: (cartResponse: CartResponse, depositData?: DepositSuccessData) => void;
  redirectToThankYouPage: (cartResponse: CartResponse) => void;
}) {
  const fullStoryTrackEvent = useFullStoryTrackEvent();
  const { runCheckoutSideEffects } = useCheckoutSideEffects();

  return useCallback(
    async function (depositData?: DepositSuccessData) {
      const cartResponse = await submitCart();

      try {
        fullStoryTrackEvent('Order Completed', getOrderCompletedPayload(cartResponse, currency, ticketMeta));
      } catch (error) {
        debug.warn('Order Completed tracking failed', error);
      }

      runCheckoutSideEffects(cartResponse);
      onOrderSubmitted?.(cartResponse, depositData);
      onSuccess?.();
      redirectToThankYouPage(cartResponse);
    },
    [submitCart, currency, ticketMeta, onSuccess, onOrderSubmitted, fullStoryTrackEvent, redirectToThankYouPage, runCheckoutSideEffects]
  );
}

export { useCallSubmitCart };
